export type LegalSection = {
  number?: string
  title: string
  intro?: string
  body?: string[]
  groups?: {
    heading: string
    items: string[]
  }[]
  outro?: string
}

export type LegalContent = {
  badge: string
  title: string
  updated?: string
  intro: string[]
  agreement?: string
  sections: LegalSection[]
  backToHome: string
}

export type LegalDocId = 'offer' | 'refund' | 'delivery' | 'replacement'

const SUPPORT_EMAIL = 'admin@pickme.kz'
const SUPPORT_PHONE = '+7 701 034 64 43'

export const LEGAL_DOCUMENTS: Record<
  LegalDocId,
  { ru: LegalContent; kk: LegalContent }
> = {
  // ─────────────────────────────────────────────────────────────────────────
  offer: {
    ru: {
      badge: 'Юридический документ',
      title:
        'Договор публичной оферты на оказание услуг доступа к платформе «meelo»',
      backToHome: '← На главную',
      intro: [
        'Настоящий документ является официальным публичным предложением ТОО «Мило Диджитал Солюшнс» (БИН 250540009597) заключить договор на оказание услуг по предоставлению доступа к облачной программной платформе для управления дошкольной организацией «meelo».',
      ],
      agreement:
        'Совершая оплату и/или регистрируясь на Платформе, Держатель Карточки и Заказчик подтверждают, что полностью ознакомлены и безоговорочно согласны с условиями настоящей Оферты.',
      sections: [
        {
          number: '1',
          title: 'Общие положения',
          body: [
            '1.1. Настоящий документ является официальным публичным предложением (далее — «Оферта») Товарищества с ограниченной ответственностью «Мило Диджитал Солюшнс» (БИН 250540009597) (далее — «Предприятие» или «Исполнитель») заключить договор на оказание услуг по предоставлению доступа к облачной программной платформе для управления дошкольной организацией «meelo» (далее — «Платформа», «Услуги») на изложенных ниже условиях.',
            '1.2. В соответствии со статьями 395, 396 и 447 Гражданского кодекса Республики Казахстан настоящая Оферта является публичной. Договор, заключаемый на основании её акцепта, является договором присоединения (статья 389 Гражданского кодекса Республики Казахстан), условия которого принимаются Заказчиком не иначе как путём присоединения к Оферте в целом.',
            '1.3. Оферта может быть акцептована любым дееспособным физическим или юридическим лицом (дошкольной организацией, частным детским садом, индивидуальным предпринимателем и т.п.), имеющим намерение приобрести доступ к Платформе на территории Республики Казахстан.',
            '1.4. Оплата Услуг производится с использованием платёжной карты. Совершая оплату и/или регистрируясь на Платформе, Держатель Карточки и Заказчик подтверждают, что полностью ознакомлены и безоговорочно согласны с условиями настоящей Оферты, а также с Политикой конфиденциальности, Порядком возврата, Порядком предоставления доступа и Порядком замены, размещёнными на сайте meelo.kz.',
          ],
        },
        {
          number: '2',
          title: 'Термины и определения',
          body: [
            '2.1. Предприятие (Исполнитель) — ТОО «Мило Диджитал Солюшнс».',
            '2.2. Платформа / Услуги / «Товар» — облачное программное обеспечение «meelo», предназначенное для автоматизации управления деятельностью дошкольной организации (учёт воспитанников и групп, посещаемость, расписание, начисления и оплаты, коммуникация с законными представителями и иные функции), доступ к которому предоставляется по подписке. Для целей настоящей Оферты и требований платёжных систем термин «Товар» означает Услуги доступа к Платформе; материальный носитель не передаётся.',
            '2.3. Заказчик (Пользователь) — лицо, акцептовавшее Оферту и использующее Платформу.',
            '2.4. Держатель Карточки — физическое лицо, на чьё имя выпущена платёжная карта, либо лицо, уполномоченное произвести оплату Услуг с использованием платёжной карты.',
            '2.5. Акцепт — полное и безоговорочное принятие Оферты путём оплаты Услуг и/или регистрации учётной записи на Платформе.',
            '2.6. Подписка (Тариф) — выбранный Заказчиком объём функций и период, в течение которого предоставляется доступ к Платформе.',
          ],
        },
        {
          number: '3',
          title: 'Предмет договора',
          body: [
            '3.1. Исполнитель обязуется предоставить Заказчику доступ к Платформе (неисключительное право использования программного обеспечения) в объёме выбранного Тарифа, а Заказчик обязуется оплатить Услуги в порядке и сроки, установленные настоящей Офертой.',
            '3.2. Услуги оказываются дистанционно по модели «программное обеспечение как услуга» (SaaS); доступ предоставляется через сеть Интернет.',
            '3.3. Перечень функций, состав Тарифов и их стоимость размещаются на сайте meelo.kz. Информация о Платформе, предоставляемая на сайте, признаётся полной и достоверной в соответствии с законодательством Республики Казахстан о защите прав потребителей и о регулировании торговой деятельности.',
          ],
        },
        {
          number: '4',
          title: 'Порядок заключения договора (акцепт)',
          body: [
            '4.1. Договор считается заключённым с момента совершения Акцепта — оплаты Услуг и/или регистрации учётной записи на Платформе.',
            '4.2. Акцепт Оферты означает, что Заказчик ознакомлен с функциональными возможностями, Тарифами и стоимостью Услуг, и они ему понятны.',
          ],
        },
        {
          number: '5',
          title: 'Стоимость Услуг и порядок оплаты',
          body: [
            '5.1. Стоимость Услуг определяется выбранным Тарифом, указывается на сайте meelo.kz в тенге и включает все налоги и обязательные платежи, предусмотренные законодательством Республики Казахстан.',
            '5.2. Оплата производится в безналичном порядке с использованием платёжной карты через систему интернет-эквайринга банка. Безопасность платежей и обработка данных платёжных карт обеспечиваются банком-эквайером и платёжными системами; Предприятие не получает и не хранит полные реквизиты платёжных карт.',
            '5.3. Услуги предоставляются на условиях предоплаты, если иное прямо не указано в Тарифе.',
            '5.4. Исполнитель вправе изменять Тарифы и стоимость в одностороннем порядке с публикацией на сайте. К уже оплаченному периоду применяются условия и стоимость, действовавшие на момент оплаты.',
          ],
        },
        {
          number: '6',
          title: 'Права и обязанности сторон',
          body: [
            '6.1. Исполнитель обязуется: предоставить доступ к Платформе в объёме Тарифа; обеспечивать работоспособность Платформы и техническую поддержку; уведомлять о плановых технических работах; обрабатывать данные в соответствии с законодательством Республики Казахстан.',
            '6.2. Исполнитель вправе: приостановить доступ при нарушении Заказчиком условий Оферты или при неоплате; привлекать третьих лиц для оказания Услуг; вносить изменения и улучшения в Платформу.',
            '6.3. Заказчик обязуется: своевременно оплачивать Услуги; предоставлять достоверные данные; не передавать данные доступа третьим лицам; использовать Платформу в соответствии с её назначением и законодательством; обеспечить наличие правовых оснований (согласий законных представителей) для размещения на Платформе персональных данных воспитанников и иных лиц.',
            '6.4. Заказчик вправе: получать доступ к оплаченным Услугам и технической поддержке; отказаться от Услуг и расторгнуть договор в порядке, предусмотренном настоящей Офертой и Порядком возврата.',
          ],
        },
        {
          number: '7',
          title: 'Персональные данные и конфиденциальность',
          body: [
            '7.1. Обработка персональных данных осуществляется в соответствии с Законом Республики Казахстан «О персональных данных и их защите» от 21 мая 2013 года № 94-V и Законом Республики Казахстан «Об информатизации».',
            '7.2. Размещая на Платформе персональные данные воспитанников, их законных представителей и сотрудников, Заказчик выступает владельцем (собственником) таких данных. Исполнитель обрабатывает указанные данные исключительно по поручению и в интересах Заказчика в целях оказания Услуг. Получение согласия законных представителей несовершеннолетних на обработку их персональных данных обеспечивает Заказчик.',
            '7.3. Стороны обязуются сохранять конфиденциальность данных и не передавать их третьим лицам, кроме случаев, предусмотренных законодательством Республики Казахстан.',
          ],
        },
        {
          number: '8',
          title: 'Ответственность сторон',
          body: [
            '8.1. За неисполнение или ненадлежащее исполнение обязательств стороны несут ответственность в соответствии с законодательством Республики Казахстан.',
            '8.2. Исполнитель не несёт ответственности за сбои, вызванные действиями Заказчика, третьих лиц, провайдеров связи, а также обстоятельствами непреодолимой силы.',
            '8.3. Совокупная ответственность Исполнителя ограничивается стоимостью Услуг, оплаченных Заказчиком за период, в котором возникло нарушение, если иное не предусмотрено законодательством Республики Казахстан о защите прав потребителей.',
          ],
        },
        {
          number: '9',
          title: 'Возврат средств и расторжение договора',
          body: [
            '9.1. Порядок возврата Услуг и денежных средств определяется отдельным документом «Порядок возврата», размещённым на сайте meelo.kz и являющимся неотъемлемой частью настоящей Оферты.',
            '9.2. Каждая из сторон вправе расторгнуть договор в порядке, предусмотренном законодательством Республики Казахстан и настоящей Офертой.',
          ],
        },
        {
          number: '10',
          title: 'Форс-мажор',
          body: [
            '10.1. Стороны освобождаются от ответственности за неисполнение обязательств, если оно вызвано обстоятельствами непреодолимой силы (стихийные бедствия, аварии, действия государственных органов, массовые сбои сетей и иные обстоятельства вне разумного контроля сторон).',
          ],
        },
        {
          number: '11',
          title: 'Срок действия и изменение Оферты',
          body: [
            '11.1. Оферта вступает в силу с момента размещения на сайте meelo.kz и действует до её отзыва или замены новой редакцией.',
            '11.2. Исполнитель вправе в одностороннем порядке изменять Оферту с публикацией новой редакции на сайте. Изменения вступают в силу с момента публикации.',
            '11.3. Во всём, что не урегулировано Офертой, стороны руководствуются законодательством Республики Казахстан.',
          ],
        },
      ],
    },
    kk: {
      badge: 'Заңды құжат',
      title:
        '«meelo» платформасына қол жеткізу қызметтерін көрсету туралы жария оферта шарты',
      backToHome: '← Басты бетке',
      intro: [
        'Осы құжат «Мило Диджитал Солюшнс» жауапкершілігі шектеулі серіктестігінің (БСН 250540009597) мектепке дейінгі ұйымды басқаруға арналған «meelo» бұлттық бағдарламалық платформасына қол жеткізу қызметтерін көрсету туралы шарт жасасу жөніндегі ресми жария ұсынысы болып табылады.',
      ],
      agreement:
        'Ақы төлей отырып және/немесе Платформада тіркеле отырып, Карта ұстаушы мен Тапсырыс беруші осы Офертаның талаптарымен толық танысқанын және оларға сөзсіз келісетінін растайды.',
      sections: [
        {
          number: '1',
          title: 'Жалпы ережелер',
          body: [
            '1.1. Осы құжат «Мило Диджитал Солюшнс» жауапкершілігі шектеулі серіктестігінің (БСН 250540009597) (бұдан әрі — «Кәсіпорын» немесе «Орындаушы») мектепке дейінгі ұйымды басқаруға арналған «meelo» бұлттық бағдарламалық платформасына (бұдан әрі — «Платформа», «Қызметтер») қол жеткізу қызметтерін көрсету туралы шарт жасасу жөніндегі ресми жария ұсынысы (бұдан әрі — «Оферта») болып табылады.',
            '1.2. Қазақстан Республикасы Азаматтық кодексінің 395, 396 және 447-баптарына сәйкес осы Оферта жария болып табылады. Оны акцептеу негізінде жасалатын шарт қосылу шарты (Қазақстан Республикасы Азаматтық кодексінің 389-бабы) болып табылады, оның талаптарын Тапсырыс беруші Офертаға тұтастай қосылу жолымен ғана қабылдайды.',
            '1.3. Офертаны Қазақстан Республикасының аумағында Платформаға қол жеткізуді сатып алуға ниеті бар кез келген әрекетке қабілетті жеке немесе заңды тұлға (мектепке дейінгі ұйым, жеке балабақша, дара кәсіпкер және т.б.) акцептей алады.',
            '1.4. Қызметтерге ақы төлеу төлем картасын пайдалану арқылы жүргізіледі. Ақы төлей отырып және/немесе Платформада тіркеле отырып, Карта ұстаушы мен Тапсырыс беруші осы Офертаның, сондай-ақ meelo.kz сайтында орналастырылған Құпиялылық саясатының, Қайтару тәртібінің, Қол жеткізуді ұсыну тәртібінің және Ауыстыру тәртібінің талаптарымен толық танысқанын және оларға сөзсіз келісетінін растайды.',
          ],
        },
        {
          number: '2',
          title: 'Терминдер мен анықтамалар',
          body: [
            '2.1. Кәсіпорын (Орындаушы) — «Мило Диджитал Солюшнс» ЖШС.',
            '2.2. Платформа / Қызметтер / «Тауар» — мектепке дейінгі ұйымның қызметін автоматтандыруға арналған (тәрбиеленушілер мен топтарды есепке алу, келу-кетуді бақылау, кесте, есептеулер мен төлемдер, заңды өкілдермен байланыс және өзге де функциялар) «meelo» бұлттық бағдарламалық қамтылымы; оған қол жеткізу жазылым бойынша беріледі. Осы Оферта және төлем жүйелерінің талаптары мақсатында «Тауар» термині Платформаға қол жеткізу Қызметтерін білдіреді; материалдық жеткізгіш берілмейді.',
            '2.3. Тапсырыс беруші (Пайдаланушы) — Офертаны акцептеген және Платформаны пайдаланатын тұлға.',
            '2.4. Карта ұстаушы — атына төлем картасы шығарылған жеке тұлға не Қызметтерге төлем картасымен ақы төлеуге уәкілетті тұлға.',
            '2.5. Акцепт — Қызметтерге ақы төлеу және/немесе Платформада тіркелу жолымен Офертаны толық әрі сөзсіз қабылдау.',
            '2.6. Жазылым (Тариф) — Тапсырыс беруші таңдаған функциялар көлемі және Платформаға қол жеткізу берілетін мерзім.',
          ],
        },
        {
          number: '3',
          title: 'Шарттың мәні',
          body: [
            '3.1. Орындаушы Тапсырыс берушіге таңдалған Тариф көлемінде Платформаға қол жеткізуді (бағдарламалық қамтылымды пайдаланудың айрықша емес құқығын) беруге, ал Тапсырыс беруші Қызметтерге осы Офертада белгіленген тәртіппен және мерзімде ақы төлеуге міндеттенеді.',
            '3.2. Қызметтер «қызмет ретіндегі бағдарламалық қамтылым» (SaaS) моделі бойынша қашықтан көрсетіледі; қол жеткізу Интернет желісі арқылы беріледі.',
            '3.3. Функциялар тізбесі, Тарифтердің құрамы мен құны meelo.kz сайтында орналастырылады. Сайтта берілетін Платформа туралы ақпарат Қазақстан Республикасының тұтынушылардың құқықтарын қорғау және сауда қызметін реттеу туралы заңнамасына сәйкес толық әрі дұрыс деп танылады.',
          ],
        },
        {
          number: '4',
          title: 'Шарт жасасу тәртібі (акцепт)',
          body: [
            '4.1. Шарт Акцепт жасалған сәттен — Қызметтерге ақы төленген және/немесе Платформада есептік жазба тіркелген сәттен бастап жасалды деп есептеледі.',
            '4.2. Офертаны акцептеу Тапсырыс берушінің Платформаның функционалдық мүмкіндіктерімен, Тарифтермен және Қызметтердің құнымен танысқанын әрі олар оған түсінікті екенін білдіреді.',
          ],
        },
        {
          number: '5',
          title: 'Қызметтердің құны және төлеу тәртібі',
          body: [
            '5.1. Қызметтердің құны таңдалған Тарифпен айқындалады, meelo.kz сайтында теңгемен көрсетіледі және Қазақстан Республикасының заңнамасында көзделген барлық салықтар мен міндетті төлемдерді қамтиды.',
            '5.2. Төлем банктің интернет-эквайринг жүйесі арқылы төлем картасын пайдалана отырып қолма-қол ақшасыз нысанда жүргізіледі. Төлемдердің қауіпсіздігін және төлем карталары деректерін өңдеуді эквайер-банк пен төлем жүйелері қамтамасыз етеді; Кәсіпорын төлем карталарының толық деректемелерін алмайды және сақтамайды.',
            '5.3. Тарифте өзгеше тікелей көрсетілмесе, Қызметтер алдын ала төлеу шартымен көрсетіледі.',
            '5.4. Орындаушы Тарифтер мен құнды сайтта жариялай отырып біржақты тәртіппен өзгертуге құқылы. Бұрын төленген кезеңге төлем жасалған сәтте қолданыста болған талаптар мен құн қолданылады.',
          ],
        },
        {
          number: '6',
          title: 'Тараптардың құқықтары мен міндеттері',
          body: [
            '6.1. Орындаушы: Тариф көлемінде Платформаға қол жеткізуді беруге; Платформаның жұмысқа қабілеттілігін және техникалық қолдауды қамтамасыз етуге; жоспарлы техникалық жұмыстар туралы хабарлауға; деректерді Қазақстан Республикасының заңнамасына сәйкес өңдеуге міндеттенеді.',
            '6.2. Орындаушы: Тапсырыс беруші Оферта талаптарын бұзған немесе ақы төлемеген жағдайда қол жеткізуді тоқтата тұруға; Қызметтерді көрсету үшін үшінші тұлғаларды тартуға; Платформаға өзгерістер мен жақсартулар енгізуге құқылы.',
            '6.3. Тапсырыс беруші: Қызметтерге уақтылы ақы төлеуге; дұрыс деректер беруге; қол жеткізу деректерін үшінші тұлғаларға бермеуге; Платформаны оның мақсатына және заңнамаға сәйкес пайдалануға; Платформада тәрбиеленушілердің және өзге де тұлғалардың дербес деректерін орналастыру үшін құқықтық негіздердің (заңды өкілдердің келісімдерінің) болуын қамтамасыз етуге міндеттенеді.',
            '6.4. Тапсырыс беруші: төленген Қызметтерге және техникалық қолдауға қол жеткізуге; осы Офертада және Қайтару тәртібінде көзделген тәртіппен Қызметтерден бас тартуға және шартты бұзуға құқылы.',
          ],
        },
        {
          number: '7',
          title: 'Дербес деректер және құпиялылық',
          body: [
            '7.1. Дербес деректерді өңдеу Қазақстан Республикасының «Дербес деректер және оларды қорғау туралы» 2013 жылғы 21 мамырдағы № 94-V Заңына және «Ақпараттандыру туралы» Заңына сәйкес жүзеге асырылады.',
            '7.2. Платформада тәрбиеленушілердің, олардың заңды өкілдерінің және қызметкерлердің дербес деректерін орналастыра отырып, Тапсырыс беруші осындай деректердің иесі (меншік иесі) болып табылады. Орындаушы аталған деректерді тек Тапсырыс берушінің тапсырмасы бойынша және оның мүддесінде Қызметтерді көрсету мақсатында өңдейді. Кәмелетке толмағандардың дербес деректерін өңдеуге заңды өкілдердің келісімін алуды Тапсырыс беруші қамтамасыз етеді.',
            '7.3. Тараптар деректердің құпиялылығын сақтауға және оларды Қазақстан Республикасының заңнамасында көзделген жағдайлардан басқа кезде үшінші тұлғаларға бермеуге міндеттенеді.',
          ],
        },
        {
          number: '8',
          title: 'Тараптардың жауапкершілігі',
          body: [
            '8.1. Міндеттемелерді орындамағаны немесе тиісінше орындамағаны үшін тараптар Қазақстан Республикасының заңнамасына сәйкес жауапты болады.',
            '8.2. Орындаушы Тапсырыс берушінің, үшінші тұлғалардың, байланыс провайдерлерінің әрекеттерінен, сондай-ақ еңсерілмейтін күш мән-жайларынан туындаған іркілістер үшін жауапты болмайды.',
            '8.3. Орындаушының жиынтық жауапкершілігі, егер Қазақстан Республикасының тұтынушылардың құқықтарын қорғау туралы заңнамасында өзгеше көзделмесе, бұзушылық туындаған кезеңде Тапсырыс беруші төлеген Қызметтердің құнымен шектеледі.',
          ],
        },
        {
          number: '9',
          title: 'Ақшаны қайтару және шартты бұзу',
          body: [
            '9.1. Қызметтер мен ақшалай қаражатты қайтару тәртібі meelo.kz сайтында орналастырылған және осы Офертаның ажырамас бөлігі болып табылатын «Қайтару тәртібі» жеке құжатымен айқындалады.',
            '9.2. Тараптардың әрқайсысы Қазақстан Республикасының заңнамасында және осы Офертада көзделген тәртіппен шартты бұзуға құқылы.',
          ],
        },
        {
          number: '10',
          title: 'Еңсерілмейтін күш (форс-мажор)',
          body: [
            '10.1. Тараптар міндеттемелерді орындамауы еңсерілмейтін күш мән-жайларынан (дүлей зілзалалар, апаттар, мемлекеттік органдардың әрекеттері, желілердің жаппай іркілісі және тараптардың ақылға қонымды бақылауынан тыс өзге де мән-жайлар) туындаған жағдайда жауапкершіліктен босатылады.',
          ],
        },
        {
          number: '11',
          title: 'Офертаның қолданылу мерзімі және оны өзгерту',
          body: [
            '11.1. Оферта meelo.kz сайтында орналастырылған сәттен бастап күшіне енеді және ол кері қайтарылғанға немесе жаңа редакциямен ауыстырылғанға дейін қолданылады.',
            '11.2. Орындаушы жаңа редакцияны сайтта жариялай отырып, Офертаны біржақты тәртіппен өзгертуге құқылы. Өзгерістер жарияланған сәттен бастап күшіне енеді.',
            '11.3. Офертада реттелмеген барлық мәселеде тараптар Қазақстан Республикасының заңнамасын басшылыққа алады.',
          ],
        },
      ],
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  refund: {
    ru: {
      badge: 'Юридический документ',
      title:
        'Порядок возврата Услуг Держателем Карточки Предприятию и возврата денежных средств',
      backToHome: '← На главную',
      intro: [
        'Настоящий Порядок определяет условия возврата Услуг (доступа к Платформе «meelo», далее также — «Товар») и возврата денежных средств, оплаченных Держателем Карточки Предприятию, и является неотъемлемой частью Договора публичной оферты.',
        'Поскольку предметом договора является доступ к программному обеспечению (услуга в значении подпункта 6 статьи 1 Закона Республики Казахстан «О защите прав потребителей»), результат которой не имеет материального выражения, нормы о возврате непродовольственного товара надлежащего качества применяются с учётом характера Услуг.',
      ],
      sections: [
        {
          title: 'Возврат до начала использования',
          body: [
            'Если доступ к Платформе ещё не был активирован и Услуги фактически не оказывались, Держатель Карточки вправе отказаться от Услуг и потребовать возврата уплаченной суммы в полном объёме.',
          ],
        },
        {
          title: 'Возврат после начала использования',
          body: [
            'При отказе от Услуг после активации доступа возврату подлежат денежные средства за оплаченный, но неиспользованный период подписки, рассчитанные пропорционально. Стоимость фактически оказанных Услуг (за использованный период) возврату не подлежит.',
          ],
        },
        {
          title: 'Возврат при ненадлежащем качестве',
          body: [
            'Если Услуги оказаны с существенными недостатками и они не устранены Исполнителем в разумный срок, Держатель Карточки вправе потребовать возврата денежных средств за соответствующий период в полном объёме (см. также «Порядок замены»).',
          ],
        },
        {
          title: 'Порядок обращения',
          body: [
            `Для возврата Держатель Карточки направляет заявление на электронную почту ${SUPPORT_EMAIL} или иным указанным на сайте способом. В заявлении указываются: фамилия, имя, отчество (при наличии) Держателя Карточки; дата и сумма платежа; номер заказа/учётной записи; причина возврата; контактные данные.`,
          ],
        },
        {
          title: 'Способ возврата денег',
          body: [
            'Возврат денежных средств производится тем же способом и на ту же платёжную карту, с которой была произведена оплата. Возврат на другую карту или иным способом не производится (требование платёжных систем). Наличными денежные средства не возвращаются.',
          ],
        },
        {
          title: 'Срок возврата',
          body: [
            'Решение по заявлению принимается в течение 10 (десяти) рабочих дней. При положительном решении денежные средства перечисляются на платёжную карту в течение 14 (четырнадцати) календарных дней с даты согласования возврата. Срок фактического зачисления средств на счёт зависит от банка-эмитента карты и может составлять до 30 (тридцати) календарных дней.',
            'Удержанные банком и платёжными системами комиссии за проведение и возврат платежа (при их наличии) могут вычитаться из суммы возврата в случаях, предусмотренных правилами платёжных систем.',
            'Возврат денежных средств осуществляется в тенге в сумме, фактически оплаченной Держателем Карточки.',
          ],
        },
      ],
    },
    kk: {
      badge: 'Заңды құжат',
      title:
        'Карта ұстаушының Қызметтерді Кәсіпорынға қайтару және ақшалай қаражатты қайтару тәртібі',
      backToHome: '← Басты бетке',
      intro: [
        'Осы Тәртіп Карта ұстаушы Кәсіпорынға төлеген Қызметтерді («meelo» платформасына қол жеткізуді, бұдан әрі — «Тауар») қайтару және ақшалай қаражатты қайтару шарттарын айқындайды әрі Жария оферта шартының ажырамас бөлігі болып табылады.',
        'Шарттың мәні бағдарламалық қамтылымға қол жеткізу (Қазақстан Республикасының «Тұтынушылардың құқықтарын қорғау туралы» Заңының 1-бабы 6-тармақшасы мағынасындағы, нәтижесі материалдық көрініске ие емес қызмет) болғандықтан, тиісті сападағы азық-түлік емес тауарды қайтару нормалары Қызметтердің сипатын ескере отырып қолданылады.',
      ],
      sections: [
        {
          title: 'Пайдалануды бастағанға дейін қайтару',
          body: [
            'Егер Платформаға қол жеткізу әлі белсендірілмеген және Қызметтер іс жүзінде көрсетілмеген болса, Карта ұстаушы Қызметтерден бас тартуға және төленген соманы толық көлемде қайтаруды талап етуге құқылы.',
          ],
        },
        {
          title: 'Пайдалануды бастағаннан кейін қайтару',
          body: [
            'Қол жеткізу белсендірілгеннен кейін Қызметтерден бас тартқан кезде жазылымның төленген, бірақ пайдаланылмаған кезеңі үшін пропорционалды есептелген ақшалай қаражат қайтарылуға жатады. Іс жүзінде көрсетілген Қызметтердің (пайдаланылған кезең үшін) құны қайтарылмайды.',
          ],
        },
        {
          title: 'Тиісті емес сападағы кездегі қайтару',
          body: [
            'Егер Қызметтер елеулі кемшіліктермен көрсетілсе және оларды Орындаушы ақылға қонымды мерзімде жоймаса, Карта ұстаушы тиісті кезең үшін ақшалай қаражатты толық көлемде қайтаруды талап етуге құқылы («Ауыстыру тәртібін» де қараңыз).',
          ],
        },
        {
          title: 'Жүгіну тәртібі',
          body: [
            `Қайтару үшін Карта ұстаушы ${SUPPORT_EMAIL} электрондық поштасына немесе сайтта көрсетілген өзге тәсілмен өтініш жібереді. Өтініште көрсетіледі: Карта ұстаушының тегі, аты, әкесінің аты (бар болса); төлемнің күні мен сомасы; тапсырыс/есептік жазба нөмірі; қайтару себебі; байланыс деректері.`,
          ],
        },
        {
          title: 'Ақшаны қайтару тәсілі',
          body: [
            'Ақшалай қаражат төлем жасалған сол тәсілмен және сол төлем картасына қайтарылады. Басқа картаға немесе өзге тәсілмен қайтару жүргізілмейді (төлем жүйелерінің талабы). Ақшалай қаражат қолма-қол түрде қайтарылмайды.',
          ],
        },
        {
          title: 'Қайтару мерзімі',
          body: [
            'Өтініш бойынша шешім 10 (он) жұмыс күні ішінде қабылданады. Оң шешім қабылданған жағдайда ақшалай қаражат қайтару келісілген күннен бастап 14 (он төрт) күнтізбелік күн ішінде төлем картасына аударылады. Қаражаттың шотқа іс жүзінде есепке алыну мерзімі картаны шығарған банкке байланысты және 30 (отыз) күнтізбелік күнге дейін болуы мүмкін.',
            'Төлемді жүргізгені және қайтарғаны үшін банк пен төлем жүйелері ұстаған комиссиялар (бар болса) төлем жүйелерінің қағидаларында көзделген жағдайларда қайтару сомасынан шегерілуі мүмкін.',
            'Ақшалай қаражат теңгемен, Карта ұстаушы іс жүзінде төлеген сомада қайтарылады.',
          ],
        },
      ],
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  delivery: {
    ru: {
      badge: 'Юридический документ',
      title: 'Порядок и сроки предоставления доступа (поставки Услуг)',
      backToHome: '← На главную',
      intro: [
        'Услуги (доступ к Платформе «meelo», далее также — «Товар») предоставляются в электронном виде дистанционно, через сеть Интернет. Физическая доставка не осуществляется, материальный носитель не передаётся, расходы на доставку отсутствуют.',
      ],
      sections: [
        {
          title: 'Способ предоставления',
          body: [
            'Доступ к Платформе предоставляется через личный кабинет на сайте meelo.kz. Данные для доступа (логин, ссылка на активацию) направляются на адрес электронной почты, указанный Заказчиком при регистрации.',
          ],
        },
        {
          title: 'Срок предоставления',
          body: [
            'Доступ к Платформе активируется автоматически непосредственно после поступления оплаты. В случае необходимости ручной обработки заказа доступ предоставляется в срок не позднее 1 (одного) рабочего дня с момента подтверждения оплаты.',
          ],
        },
        {
          title: 'Срок действия доступа',
          body: [
            'Доступ к Платформе предоставляется на период оплаченной подписки (Тарифа) в соответствии с условиями, указанными на сайте meelo.kz.',
          ],
        },
        {
          title: 'Задержка предоставления',
          body: [
            `Если доступ не был предоставлен в указанный срок по причинам, зависящим от Исполнителя, Заказчик вправе обратиться в службу поддержки по адресу ${SUPPORT_EMAIL} или телефону ${SUPPORT_PHONE}. Исполнитель устраняет причину и предоставляет доступ в кратчайший срок либо по требованию Заказчика возвращает денежные средства в порядке, установленном «Порядком возврата».`,
            'Обязательство Исполнителя по предоставлению Услуг считается исполненным с момента предоставления Заказчику доступа к Платформе.',
          ],
        },
      ],
    },
    kk: {
      badge: 'Заңды құжат',
      title: 'Қол жеткізуді ұсыну (Қызметтерді жеткізу) тәртібі мен мерзімдері',
      backToHome: '← Басты бетке',
      intro: [
        'Қызметтер («meelo» платформасына қол жеткізу, бұдан әрі — «Тауар») электрондық түрде, қашықтан, Интернет желісі арқылы ұсынылады. Физикалық жеткізу жүзеге асырылмайды, материалдық жеткізгіш берілмейді, жеткізу шығыстары жоқ.',
      ],
      sections: [
        {
          title: 'Ұсыну тәсілі',
          body: [
            'Платформаға қол жеткізу meelo.kz сайтындағы жеке кабинет арқылы беріледі. Қол жеткізу деректері (логин, белсендіру сілтемесі) Тапсырыс беруші тіркелу кезінде көрсеткен электрондық пошта мекенжайына жіберіледі.',
          ],
        },
        {
          title: 'Ұсыну мерзімі',
          body: [
            'Платформаға қол жеткізу төлем түскеннен кейін бірден автоматты түрде белсендіріледі. Тапсырысты қолмен өңдеу қажет болған жағдайда қол жеткізу төлем расталған сәттен бастап 1 (бір) жұмыс күнінен кешіктірілмей беріледі.',
          ],
        },
        {
          title: 'Қол жеткізудің қолданылу мерзімі',
          body: [
            'Платформаға қол жеткізу meelo.kz сайтында көрсетілген талаптарға сәйкес төленген жазылым (Тариф) кезеңіне беріледі.',
          ],
        },
        {
          title: 'Ұсынудың кешіктірілуі',
          body: [
            `Егер қол жеткізу Орындаушыға байланысты себептермен көрсетілген мерзімде ұсынылмаса, Тапсырыс беруші ${SUPPORT_EMAIL} мекенжайы немесе ${SUPPORT_PHONE} телефоны бойынша қолдау қызметіне жүгінуге құқылы. Орындаушы себепті жояды және қол жеткізуді мүмкіндігінше қысқа мерзімде ұсынады не Тапсырыс берушінің талабы бойынша «Қайтару тәртібінде» белгіленген тәртіппен ақшалай қаражатты қайтарады.`,
            'Орындаушының Қызметтерді ұсыну жөніндегі міндеттемесі Тапсырыс берушіге Платформаға қол жеткізу ұсынылған сәттен бастап орындалды деп есептеледі.',
          ],
        },
      ],
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  replacement: {
    ru: {
      badge: 'Юридический документ',
      title:
        'Порядок замены Товара Предприятием Держателю Карточки в случае поставки некачественного и/или некомплектного Товара',
      backToHome: '← На главную',
      intro: [
        'Настоящий Порядок применяется в случаях, когда Услуги (доступ к Платформе «meelo», далее также — «Товар») предоставлены с недостатками — то есть Платформа недоступна, работает со сбоями или её функциональные возможности не соответствуют описанию, размещённому на сайте meelo.kz (некачественный и/или некомплектный «Товар»).',
      ],
      sections: [
        {
          title: 'Уведомление',
          body: [
            `При обнаружении недостатков Держатель Карточки (Заказчик) направляет уведомление в службу поддержки на электронную почту ${SUPPORT_EMAIL} или телефону ${SUPPORT_PHONE} с описанием недостатка, даты его обнаружения и данных учётной записи.`,
          ],
        },
        {
          title: 'Устранение недостатков',
          body: [
            'Исполнитель проверяет обращение и устраняет недостаток (восстанавливает доступ, исправляет сбой, предоставляет недостающий функционал) в разумный срок, как правило не позднее 3 (трёх) рабочих дней с момента обращения. Сложные технические недостатки устраняются в срок, дополнительно согласованный с Заказчиком.',
          ],
        },
        {
          title: 'Замена',
          body: [
            'Если недостаток не может быть устранён, Исполнитель по согласованию с Держателем Карточки предоставляет равноценный доступ (заменяет Тариф/функционал на аналогичный надлежащего качества) либо продлевает оплаченный период на срок, в течение которого Услуги были недоступны.',
          ],
        },
        {
          title: 'Возврат при невозможности замены',
          body: [
            'Если устранение недостатка или равноценная замена невозможны, Держатель Карточки вправе потребовать возврата денежных средств за соответствующий период в порядке, установленном «Порядком возврата».',
          ],
        },
        {
          title: 'Компенсация периода простоя',
          body: [
            'За период, в течение которого Платформа была недоступна по вине Исполнителя, оплаченный срок подписки продлевается на соответствующее количество дней без дополнительной оплаты.',
            'Настоящий Порядок применяется с учётом прав потребителей, предусмотренных Законом Республики Казахстан «О защите прав потребителей» от 4 мая 2010 года № 274-IV, на надлежащее качество товара (работы, услуги).',
          ],
        },
      ],
    },
    kk: {
      badge: 'Заңды құжат',
      title:
        'Сапасыз және/немесе жиынтықталмаған тауар жеткізілген жағдайда Кәсіпорынның Карта ұстаушыға тауарды ауыстыру тәртібі',
      backToHome: '← Басты бетке',
      intro: [
        'Осы Тәртіп Қызметтер («meelo» платформасына қол жеткізу, бұдан әрі — «Тауар») кемшіліктермен ұсынылған жағдайларда, яғни Платформа қолжетімсіз болса, іркіліспен жұмыс істесе немесе оның функционалдық мүмкіндіктері meelo.kz сайтында орналастырылған сипаттамаға сәйкес келмесе (сапасыз және/немесе жиынтықталмаған «Тауар») қолданылады.',
      ],
      sections: [
        {
          title: 'Хабарлама',
          body: [
            `Кемшіліктер анықталған кезде Карта ұстаушы (Тапсырыс беруші) ${SUPPORT_EMAIL} электрондық поштасына немесе ${SUPPORT_PHONE} телефонына кемшіліктің сипаттамасын, оның анықталған күнін және есептік жазба деректерін көрсете отырып хабарлама жібереді.`,
          ],
        },
        {
          title: 'Кемшіліктерді жою',
          body: [
            'Орындаушы жүгінуді тексереді және кемшілікті (қол жеткізуді қалпына келтіреді, іркілісті түзетеді, жетіспейтін функционалды ұсынады) ақылға қонымды мерзімде, әдетте жүгінген сәттен бастап 3 (үш) жұмыс күнінен кешіктірмей жояды. Күрделі техникалық кемшіліктер Тапсырыс берушімен қосымша келісілген мерзімде жойылады.',
          ],
        },
        {
          title: 'Ауыстыру',
          body: [
            'Егер кемшілік жойылмаса, Орындаушы Карта ұстаушымен келісім бойынша тең құнды қол жеткізуді ұсынады (Тарифті/функционалды тиісті сападағы баламалымен ауыстырады) не Қызметтер қолжетімсіз болған мерзімге төленген кезеңді ұзартады.',
          ],
        },
        {
          title: 'Ауыстыру мүмкін болмаған кездегі қайтару',
          body: [
            'Егер кемшілікті жою немесе тең құнды ауыстыру мүмкін болмаса, Карта ұстаушы тиісті кезең үшін ақшалай қаражатты «Қайтару тәртібінде» белгіленген тәртіппен қайтаруды талап етуге құқылы.',
          ],
        },
        {
          title: 'Тоқтап тұру кезеңін өтеу',
          body: [
            'Платформа Орындаушының кінәсінен қолжетімсіз болған кезең үшін төленген жазылым мерзімі тиісті күн санына қосымша ақысыз ұзартылады.',
            'Осы Тәртіп Қазақстан Республикасының «Тұтынушылардың құқықтарын қорғау туралы» 2010 жылғы 4 мамырдағы № 274-IV Заңында көзделген тұтынушылардың тауардың (жұмыстың, қызметтің) тиісті сапасына құқықтарын ескере отырып қолданылады.',
          ],
        },
      ],
    },
  },
}
